<template>
  <div v-if="canPreview" class="relative" data-role-preview-dropdown>
    <button
      type="button"
      class="flex items-center space-x-1 px-2 py-1 rounded-md cursor-pointer"
      :class="isPreviewActive
        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
        : 'text-gray-600 hover:bg-gray-100'"
      @click="toggleDropdown"
    >
      <font-awesome-icon :icon="['fas', 'eye']" class="w-4 h-4" />
      <span v-if="isPreviewActive" class="text-xs font-medium">Preview</span>
    </button>

    <div
      v-if="dropdownOpen"
      class="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50"
      @click.stop
    >
      <div class="px-4 py-3 border-b border-gray-200">
        <p class="text-sm font-medium text-gray-900">Role Preview</p>
        <p class="text-xs text-gray-500 mt-1">Select roles to preview</p>
      </div>

      <div class="py-2 max-h-60 overflow-y-auto">
        <label
          v-for="role in availableRoles"
          :key="role.role_id"
          class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
        >
          <input
            type="checkbox"
            :value="role.role_id"
            v-model="selectedRoles"
            class="mr-3 rounded border-gray-300 text-tangerine-500 focus:ring-tangerine-500"
          />
          <span>{{ role.display_name }}</span>
        </label>
        <p
          v-if="availableRoles.length === 0"
          class="px-4 py-3 text-xs text-gray-500 italic"
        >
          No roles available to preview. Admin and Security roles cannot be previewed.
        </p>
      </div>

      <div class="px-4 py-3 border-t border-gray-200 flex items-center space-x-2">
        <button
          type="button"
          class="btn-primary text-xs px-3 py-1.5"
          :disabled="selectedRoles.length === 0"
          @click="applyPreview"
        >
          Apply
        </button>
        <button
          v-if="isPreviewActive"
          type="button"
          class="btn-secondary text-xs px-3 py-1.5"
          @click="clearPreview"
        >
          Clear Preview
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { FEATURES } from '@/constants/features'
import { RESERVED_ROLE_IDS } from '@/constants/roles'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'RolePreviewDropdown',
  components: {
    FontAwesomeIcon,
  },
  data() {
    return {
      dropdownOpen: false,
      selectedRoles: [],
    }
  },
  computed: {
    ...mapGetters('rolePreview', ['isPreviewActive', 'activeRoles']),
    ...mapGetters('tenant', ['actual_tenant_roles', 'current_tenant_slug']),
    ...mapGetters('featureConfig', ['visibleRoles', 'roleFeatures']),
    ...mapGetters('auth', ['current_user']),
    availableRoles() {
      const userRoleIds = new Set(this.actual_tenant_roles)
      // Reserved roles are not previewable — simulating them would expose
      // mutation UI the backend would reject on click.
      return this.visibleRoles.filter(
        r => userRoleIds.has(r.role_id) && !RESERVED_ROLE_IDS.includes(r.role_id)
      )
    },
    canPreview() {
      if (this.current_user?.is_superuser) return true
      // Read directly from tenant_access so we don't need featureConfig loaded
      // just to decide whether to render the button in the nav bar.
      const tenantAccess = this.$store.state.tenant.tenantAccess?.[this.current_tenant_slug]
      return (tenantAccess?.features || []).includes(FEATURES.ACTION_ROLE_PREVIEW)
    },
  },
  watch: {
    isPreviewActive(active) {
      if (!active) {
        this.initSelectedRoles()
      }
    },
    actual_tenant_roles() {
      this.initSelectedRoles()
    },
  },
  mounted() {
    this.initSelectedRoles()
  },
  methods: {
    ...mapActions('rolePreview', ['enablePreview', 'disablePreview']),
    ...mapActions('featureConfig', ['fetchConfig']),
    initSelectedRoles() {
      this.selectedRoles = this.actual_tenant_roles.filter(
        r => !RESERVED_ROLE_IDS.includes(r)
      )
    },
    async toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
      if (this.dropdownOpen) {
        // Lazy-load the role catalog only when the user actually opens the
        // dropdown — rendering the button in the nav bar shouldn't trigger it.
        await this.fetchConfig()
      }
    },
    closeDropdown() {
      this.dropdownOpen = false
    },
    applyPreview() {
      this.enablePreview([...this.selectedRoles])
      this.dropdownOpen = false
    },
    clearPreview() {
      this.disablePreview()
      this.dropdownOpen = false
    },
  },
}
</script>
